import anime from 'animejs';
import { applyDynamicTextScaling, extractStylesFromObject } from '@/utils/common';
import * as _ from 'lodash';
import { TemplateLoader } from './template';
import { loadGoogleFont } from './font-loader';
import TemplatesService from '../templates';
import { NoTemplateHTMLContentError, NoTemplateObjectError } from './animation.errors';
import { logError, logWarning } from '../../utils/logs';
import './resolution-fixer';
import { animationPlayerInstance, attachVideoToTimelinePlayer, enableAnimationRecording } from '../../components/animations/AnimationController.svelte';
import { TextAnimations } from '../../types/metadata';
export const computeTime = (marker) => {
    let returnValue = 0;
    if (marker.seconds) {
        returnValue = +marker.seconds * 1000;
    }
    if (marker.nanos) {
        returnValue += +marker.nanos / 1000000;
    }
    return Math.round(returnValue);
};
export class UnableToFindOriginalHTMLStringError extends Error {
    message = 'Unable to find original HTML string';
}
export const LEGACY_ANIMATION_OBJECT = 'data-animation';
class AnimationService {
    node;
    animationState = 'pause';
    timeline;
    videoTrackingTimeline;
    captionTimeline;
    videoElement = document.createElement('video');
    videoElements = [];
    audioElements = [];
    bgVideoElements = [];
    templateLoader;
    trackingData;
    sermonClip;
    animations = [];
    metadata;
    audioTracks = [];
    updateFn = null;
    adjustedForProduction = false;
    onCompleteTemplateLoadCallback;
    oldMeta = null;
    metaChanges = [];
    textNodes = [];
    captureEndpoint = false;
    attachedVideoSrc;
    captionParentNodes = [];
    zoomPercentage = 1;
    isProduction = false;
    totalDuration = 0;
    audioLevels = 100;
    videoLevels = 100;
    buildingTrackingData = false;
    cachedTranscriptionData = null;
    cachedSelectedSentences = null;
    cachedDelays = null;
    constructor(node = document.createElement('div')) {
        this.node = node;
        document.addEventListener('visibilitychange', () => this.onVisibilityStateChanged());
        let timeoutResize;
        window.addEventListener('resize', () => {
            if (!this.node || !this.templateLoader)
                return;
            try {
                if (this.templateLoader) {
                    if (timeoutResize) {
                        clearTimeout(timeoutResize);
                    }
                    timeoutResize = setTimeout(() => {
                        console.log('Resizing...');
                        this.templateLoader.adjustDisplayForPreview();
                    }, 1000);
                }
            }
            catch (err) {
                logError('Problem with adjusting after resize', err);
            }
        });
        this.node.style.position = 'relative';
    }
    get progress() {
        return (this.timeline.currentTime / this.timeline.duration) * 100;
    }
    seek(progressPercentage) {
        this.timeline.seek(this.timeline.duration * progressPercentage);
    }
    pauseAllVideoElements(skipSermon = false) {
        document.querySelectorAll('video').forEach(node => {
            if (node.parentElement?.getAttribute('data-amelement') &&
                node.parentElement?.getAttribute('data-amelement') == 'sermonclip') {
                if (skipSermon)
                    return;
            }
            if (!node.paused)
                node.pause();
        });
    }
    async resetVideoSeek() {
        this.videoElements.forEach(videoElement => {
            try {
                videoElement.currentTime = 1;
            }
            catch (err) {
                console.warn('There was a problem setting the video element current time', err, 1);
            }
        });
    }
    async seekAudio(progress) {
        if (!this.timeline)
            return 0;
        if (progress == undefined) {
            progress = 1;
            if (!isFinite(this.timeline.progress))
                console.warn('Timeline progress is not finite', this.timeline.progress);
            else if (!isFinite(this.timeline.duration))
                console.warn('Timeline duration is not finite', this.timeline.duration);
            else
                progress = this.timeline.progress;
        }
        const targetTime = Math.floor(this.totalDuration * (progress / 100));
        this.audioElements.forEach(audioElement => {
            try {
                audioElement.currentTime = targetTime;
            }
            catch (err) {
                console.warn('There was a problem setting the audio element current time', err, targetTime);
            }
        });
    }
    async seekVideo() {
        let progress = 1;
        if (!isFinite(this.timeline.progress))
            console.warn('Timeline progress is not finite', this.timeline.progress);
        else if (!isFinite(this.timeline.duration))
            console.warn('Timeline duration is not finite', this.timeline.duration);
        else
            progress = this.timeline.progress;
        //console.log('VideoSeekProgress', progress);
        const targetTime = Math.floor(this.totalDuration * (progress / 100));
        this.videoElements.forEach(videoElement => {
            try {
                videoElement.currentTime = targetTime;
            }
            catch (err) {
                console.warn('There was a problem setting the video element current time', err, targetTime);
            }
        });
        this.audioElements.forEach(audioElement => {
            try {
                audioElement.currentTime = targetTime;
            }
            catch (err) {
                console.warn('There was a problem setting the audio element current time', err, targetTime);
            }
        });
        // try {
        //   await waitForLoad(sermonclip);
        // } catch (err) {
        //   console.warn("Could not wait for load", err);
        // }
    }
    getDom() {
        return this.node;
    }
    get duration() {
        return Math.floor(this.timeline.duration / 1000);
    }
    get templateMusic() {
        return this.metadata.music;
    }
    setMusicVolume(volume) {
        this.audioLevels = volume;
        this.adjustAudioVolume();
    }
    setVideoVolume(volume) {
        this.videoLevels = volume;
        this.adjustAudioVolume();
    }
    setMetaMusic(music) {
        if (!this.metadata)
            this.metadata = {};
        this.metadata.music = music;
        this.buildMetaMusic();
        const isCurrentlyPlaying = window.playingStatus === 'play';
        this.pause();
        if (isCurrentlyPlaying)
            this.play();
    }
    isPlaying = false;
    async play(skip = false, reset = false, cb) {
        if (this.isPlaying && !this.isCompleted)
            return this.pause();
        if (!this.isPlaying)
            this.isPlaying = true;
        document.querySelectorAll('.audioPreviewElement').forEach(element => {
            if (element.paused == false)
                element.pause();
        });
        if (this.isCompleted)
            this.restartTimeline();
        window.playingStatus = 'play';
        this.animationState = 'play';
        // if (!skip && !isSafari()) await this.seekVideo();
        // else this.seekVideo();
        if (!reset)
            this.playAudio();
        this.timeline.play();
        if (this.videoTrackingTimeline)
            this.videoTrackingTimeline.play();
        if (reset) {
            let time = this.timeline.duration > 6000 ? 5000 : this.timeline.duration / 2;
            setTimeout(() => {
                window.playingStatus = 'pause';
                this.animationState = 'pause';
                setTimeout(async () => {
                    this.resetVideoSeek();
                    setTimeout(() => {
                        this.timeline.begin = () => {
                            if (cb)
                                cb();
                            try {
                                if (window.electronAPI) {
                                    this.captureEndpoint = true;
                                    window.electronAPI.startRecording();
                                }
                                else {
                                    console.warn('ELECTRON API DOES NOT EXIST.');
                                }
                            }
                            catch (err) {
                                console.warn(err);
                            }
                        };
                        this.timeline.restart();
                        this.seekVideo();
                        try {
                            if (window.electronAPI) {
                                window.electronAPI.startRecording();
                            }
                        }
                        catch (err) {
                            // Do nothing...
                        }
                        window.productionRecording = true;
                        window.playingStatus = 'play';
                        this.animationState = 'play';
                        // if(cb) cb();
                    }, 1000);
                }, 500);
            }, time);
        }
    }
    pause() {
        this.isPlaying = false;
        window.playingStatus = 'pause';
        this.animationState = 'pause';
        if (this.timeline)
            this.timeline.pause();
        if (this.videoTrackingTimeline)
            this.videoTrackingTimeline.pause();
        this.pauseAudio();
    }
    onVisibilityStateChanged() {
        if (this.isProduction)
            return;
        console.warn('Visibility state changed', document.visibilityState);
        if (this.animationState == 'pause')
            return;
        if (document.visibilityState == 'hidden') {
            this.pauseAudio();
        }
        else {
            this.play();
        }
    }
    async waitForTrackingDataToResolve() {
        return new Promise((resolve, reject) => {
            let interval = setInterval(() => {
                if (this.buildingTrackingData)
                    return;
                clearInterval(interval);
                resolve(true);
            }, 250);
        });
    }
    setMetaData(metadata, append = false) {
        //console.log('setting metadata', metadata);
        this.oldMeta = this.metadata ? { ...this.metadata } : null;
        if (append)
            this.metadata = { ...this.metadata, ...metadata };
        else
            this.metadata = metadata;
        return this;
    }
    rebuildTranscriptionAnimationData(animationName = 'FadeIn') {
        console.log('Rebuilding hte transcription animations data');
        return this.buildTranscriptionAnimationData(this.cachedTranscriptionData, this.cachedSelectedSentences, this.cachedDelays, animationName);
    }
    buildTranscriptionAnimationData(transcriptionObject, selectedSentences, delays = [], animationName = 'FadeIn') {
        // Future proof fix for animation name
        if (animationName && !TextAnimations.includes(animationName)) {
            animationName = 'FadeIn';
        }
        // Cache the input data for later use
        this.cachedTranscriptionData = transcriptionObject;
        this.cachedSelectedSentences = selectedSentences;
        this.cachedDelays = delays;
        let animations = [];
        let runningDelay = 0;
        const words = transcriptionObject.transcription;
        let averageWordSpeed = 500; // this is a constant that really doesnt matter at this point\
        if (!delays)
            delays = []; // Fix for when delays are an empty array
        selectedSentences.forEach((sentence, index) => {
            const parentNode = document.createElement('div');
            // parentNode.style.textAlign = 'center';
            for (let i = 0; i < sentence.words.length; i++) {
                const selectedWord = sentence.words[i];
                const word = words[selectedWord.index];
                if (!word)
                    continue;
                const nextSelectWord = sentence.words[i + 1];
                let nextWord = null;
                if (nextSelectWord) {
                    nextWord = words[nextSelectWord.index];
                }
                const start = computeTime(word.startTime);
                const delayData = delays.find((delay) => delay.wordIndex == selectedWord.index);
                const end = computeTime(word.endTime);
                let duration = end - start;
                let delayedAdded = 0;
                if (delayData) {
                    if (delayData.delay > 0) {
                        duration += delayData.delay;
                        delayedAdded = delayData.delay;
                    }
                }
                const isFirstWordInSentence = i === 0;
                const isLastWordInSentence = i === sentence.words.length - 1;
                const isLastWordInVideo = i === sentence.words.length - 1 && index == selectedSentences.length - 1;
                if (isLastWordInSentence) {
                    const nextSentence = selectedSentences[index + 1];
                    if (nextSentence) {
                        const nextSentenceFirstWord = words[nextSentence.words[0].index];
                        if (nextSentenceFirstWord.index - word.index == 1) {
                            // Skip something here for continuing sentences
                        }
                        else {
                            // duration += 200;
                        }
                    }
                    else {
                        // duration += 200;
                    }
                }
                if (isLastWordInSentence) {
                    let nextSentence = selectedSentences[index + 1];
                    if (nextSentence) {
                        if (nextSentence.index - 1 === sentence.index) {
                            if (delayedAdded) {
                                duration -= delayedAdded;
                            }
                            const lastWord = words[sentence.words[sentence.words.length - 1].index]; // get the last word of the current sentence
                            const firstWord = words[nextSentence.words[0].index]; // and the first word of the next sentenence
                            const delay = computeTime(firstWord.startTime) - computeTime(lastWord.endTime); //  And compute for the difference in time between to the two to add a "buffer" between them.
                            duration += delay; // Add that to the running delay.
                        }
                    }
                }
                animations.push({
                    delay: runningDelay,
                    duration: duration,
                    animation: animationName,
                    word: selectedWord.word,
                    isFirstWordInSentence,
                    isLastWordInSentence,
                    parentNode
                });
                runningDelay += duration;
                if (nextWord && !isLastWordInSentence) {
                    const nextStart = computeTime(nextWord.startTime);
                    const delayed = nextStart - end;
                    runningDelay += delayed;
                }
            }
        });
        this.animations = animations;
        return this;
    }
    onUpdate(fn) {
        this.updateFn = fn;
    }
    clearUpdate() {
        this.updateFn = null;
    }
    reset() {
        return this;
    }
    runProduction() {
        this.isProduction = true;
        // if (this.metadata) this.buildMetadataDisplay();
        // this.adjustDisplay();
        this.node.style.margin = '0px';
        const finalImageDom = this.node.querySelector('#finalImageToExport');
        const playanimationnow = document.createElement('div');
        playanimationnow.id = 'playAnimationNow';
        if (finalImageDom) {
            finalImageDom.style.margin = '0px';
        }
        this.node.querySelectorAll('video, audio').forEach(media => {
            // Remember to turn this bakc on.
            media.muted = true;
        });
        // this.timeline.seek(1);
        setTimeout(() => {
            // this.play(true, true, () => {
            //   document.body.append(playanimationnow);
            //   // New Electorn JS code test
            // });
            enableAnimationRecording();
            animationPlayerInstance.seek(0);
            animationPlayerInstance.play();
        }, 2500);
    }
    reattachVideo() {
        console.log('Reattaching video src', this.attachedVideoSrc);
        this.attachVideoObject(this.attachedVideoSrc);
    }
    delay(time) {
        return new Promise(resolve => setTimeout(resolve, time));
    }
    async loadVideo(videoDom, tries, timeout = 15000) {
        return new Promise((resolve, reject) => {
            if (this.sermonClip) {
                return resolve(true);
            }
            let forceLoadTime = setTimeout(() => {
                // For now we will force the video to play.
                this.sermonClip = videoDom;
                resolve(true);
            }, 5000);
            videoDom.onloadedmetadata = () => {
                clearTimeout(forceLoadTime);
                this.sermonClip = videoDom;
                resolve(true);
            };
            const readyState = +videoDom.readyState;
            // Check if video is loaded
            videoDom.load();
            if (readyState > 0) {
                clearTimeout(forceLoadTime);
                this.sermonClip = videoDom;
                return resolve(true);
            }
        });
    }
    appendToVideoElements(videoDom) {
        if (this.videoElements.indexOf(videoDom) !== -1)
            return;
        this.videoElements.push(videoDom);
    }
    /**
     * We are making this change because of a fundamental change in
     */
    async prepareSermonclipForProduction() {
        try {
            let mainVideo;
            let hasMain = false;
            const targetNodes = this.node.querySelectorAll('[data-amelement="sermonclip"]');
            if (!targetNodes.length)
                return;
            else {
                const videoDomElement = targetNodes[0].querySelector('video');
                if (videoDomElement)
                    mainVideo = videoDomElement;
            }
            for (const targetNode of targetNodes) {
                const videoDom = targetNode.querySelector('video');
                if (videoDom) {
                    try {
                        await this.loadVideo(videoDom, 1);
                    }
                    catch (err) {
                        console.warn('Could not load video...', videoDom, err);
                    }
                    this.appendToVideoElements(videoDom);
                    if (!hasMain) {
                        const xPos = targetNode.getBoundingClientRect().x;
                        const yPos = targetNode.getBoundingClientRect().y;
                        if (xPos < -50 || yPos < -50)
                            continue;
                        mainVideo = videoDom;
                        if (targetNode.getAttribute('data-notes') == 'main') {
                            hasMain = true;
                            continue;
                        }
                    }
                }
            }
            // @ts-ignore
            if (mainVideo) {
                attachVideoToTimelinePlayer(mainVideo);
            }
            else {
                throw new Error('[production render issue] Main video not defined');
            }
        }
        catch (err) {
            console.warn('Could not find a sermon clip to prepare.');
        }
    }
    /**
     * this is only a fix to be applied inside of the preview, what this does is it fixes the video container so that aicc vertical videos are not stretched horizontally
     */
    async prepareAICC() {
        if (!this.sermonClip)
            return null; // No semron clip was defined
        this.sermonClip.style.objectFit = 'contain';
    }
    /**
     *
     * @param src THE SOURCE OF THE VIDEO (URL)
     * @param seekToCurrentTime This value is to be used if you are replacing the video and you want the app to seek to the current time of the video that already exists. Usefull when replacing a lower quality source to a higher quality source. (i.e. 420p to 1080p)
     * @returns void
     */
    async attachVideoObject(src, seekToCurrentTime = false) {
        // console.log('attaching video object', src);
        let cachedTime = 0;
        if (seekToCurrentTime) {
            if (this.sermonClip) {
                cachedTime = this.sermonClip.currentTime;
            }
        }
        this.attachedVideoSrc = src;
        this.videoElements = [];
        this.sermonClip = null;
        const targetNodes = this.node.querySelectorAll('[data-amelement="sermonclip"]');
        let index = 0;
        let mainVideo;
        let hasMain = false;
        for (const targetNode of targetNodes) {
            // console.log('processing', targetNode);
            let videoNeedsToBeAppended = false;
            let videoDom = targetNode.querySelector('video'); // document.createElement('video');
            let source;
            if (!videoDom) {
                videoNeedsToBeAppended = true;
                videoDom = document.createElement('video');
                source = document.createElement('source');
            }
            else {
                source = videoDom.querySelector('source'); // document.createElement('source');
            }
            if (targetNode.querySelector('.stampImg')) {
                let stampStyles = extractStylesFromObject(targetNode.querySelector('.stampImg'));
                videoDom.style.cssText = stampStyles;
            }
            source.src = src;
            if (this.node) {
                if (targetNode && videoNeedsToBeAppended) {
                    videoDom.appendChild(source);
                    Object.assign(videoDom.style, {
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        playsinline: true
                    });
                    targetNode.innerHTML = '';
                    if (targetNode.childNodes.length > 0) {
                        targetNode.insertBefore(videoDom, targetNode.firstChild);
                    }
                    else {
                        // The target node for some reason is empty.
                        targetNode.append(videoDom);
                    }
                }
                else if (videoNeedsToBeAppended)
                    this.node.append(videoDom);
            }
            videoDom.playsInline = true;
            videoDom.muted = true;
            try {
                await this.loadVideo(videoDom, 1);
                if (seekToCurrentTime) {
                    videoDom.currentTime = cachedTime;
                }
            }
            catch (err) {
                console.warn('Could not load video...', videoDom, err);
            }
            if (!hasMain) {
                mainVideo = videoDom;
                if (targetNode.getAttribute('sermonclip')) {
                    hasMain = true;
                    continue;
                }
            }
            this.bgVideoElements.push(videoDom);
        }
        //@ts-ignore for some reason this doesnt work.
        if (mainVideo) {
            mainVideo.muted = false;
            this.appendToVideoElements(mainVideo);
            attachVideoToTimelinePlayer(mainVideo);
        }
        // console.log("VIDEO ELEMENTS", this.videoElements);
        if (hasMain) {
            // We only want to add the animation if there are actually
            this.bgVideoElements.forEach(videoElement => {
                const el = videoElement.parentElement;
                if (el) {
                    const playVideoObject = {
                        animations: [
                            {
                                animation: 'PlayVideo',
                                animationDuration: '1',
                                animationDelay: '0'
                            }
                        ]
                    };
                    el.setAttribute('data-animation', JSON.stringify(playVideoObject));
                }
            });
        }
        else {
            this.bgVideoElements = [];
        }
        return this;
    }
    setTotalDuration(duration) {
        this.totalDuration = duration;
    }
    buildExtraInformation() {
        if (this.metadata.extraInformation && this.metadata.extraInformation.length) {
            this.metadata.extraInformation.forEach((item) => {
                try {
                    if (this.isProduction) {
                        console.log('[buildExtraInformation] Building extra information', item);
                    }
                    const searchTag = `[data-amelement="${item.key}"]`;
                    const element = this.node.querySelector(searchTag);
                    if (element) {
                        if (item.type == 'text') {
                            const dynamicTextElement = element.querySelector('.dynamic .ql-editor');
                            if (dynamicTextElement && !this.isProduction)
                                dynamicTextElement.innerHTML = item.value; // Do not replace these values if it is already in production.
                        }
                    }
                }
                catch (err) {
                    console.warn('[Error] Could not build extra information', err);
                }
            });
        }
    }
    detectChanges() {
        return _.reduce(this.metadata, (result, value, key) => {
            if (!this.oldMeta || !this.oldMeta[key])
                return result.concat(key);
            return _.isEqual(value, this.oldMeta[key]) ? result : result.concat(key);
        }, []);
    }
    triggerSubtitles() {
        if (this.metadata && this.metadata?.showSubtitles !== undefined) {
            const targetNode = this.node.querySelector('[data-amelement="caption"]');
            if (targetNode) {
                const style = this.metadata.showSubtitles ? 'block' : 'none';
                targetNode.style.display = style;
            }
        }
    }
    buildMetadataDisplay() {
        this.metaChanges = this.detectChanges();
        const triggers = [
            { triggers: ['title', 'showTitle'], trigger: this.buildTitle },
            { triggers: ['fontColor', 'fontFamily'], trigger: this.buildFonts },
            { triggers: ['extraInformation'], trigger: this.buildExtraInformation },
            {
                triggers: ['brandLogo', 'showBrandLogo', 'checkIfBrandLogoExists', 'brandKit'],
                trigger: this.buildBrandKit
            },
            { triggers: ['music'], trigger: this.buildMetaMusic },
            {
                triggers: ['audioLevel', 'videoLevel'],
                trigger: this.adjustAudioVolume
            },
            { triggers: ['textAnimation'], trigger: this.buildAnimations }
            // { triggers: ['showSubtitles'], trigger: this.triggerSubtitles },
        ];
        const changes = _.reduce(triggers, (result, value) => {
            return _.intersectionWith(this.metaChanges, value.triggers, _.isEqual).length
                ? result.concat(value.trigger)
                : result;
        }, []);
        // @ts-ignore
        changes.forEach((t) => {
            t.bind(this)();
        });
        this.buildExtraInformation();
    }
    rebuildChanges() {
        // this.buildTitle();
        this.buildBrandKit();
        this.buildExtraInformation();
        this.buildMetaMusic();
        this.adjustAudioVolume();
        this.buildFonts();
    }
    rebuildForRender() {
        this.buildBrandKit();
        this.buildMetaMusic();
        this.adjustAudioVolume();
        this.buildFonts();
    }
    adjustAudioVolume() {
        if (this.metadata) {
            if (this.metadata.audioLevel)
                this.audioLevels = this.metadata.audioLevel;
            if (this.metadata.videoLevel)
                this.videoLevels = this.metadata.videoLevel;
        }
        const audioVolume = this.audioLevels / 100;
        const videoVolume = this.videoLevels / 100;
        this.node.querySelectorAll('audio').forEach(audioElement => {
            audioElement.volume = audioVolume;
        });
        this.node.querySelectorAll('video').forEach(audioElement => {
            audioElement.volume = videoVolume;
        });
    }
    buildMetaMusic() {
        if (this.metadata.music && this.metadata.music !== 'default') {
            if (this.audioTracks) {
                this.audioTracks = [];
            }
            this.audioTracks.push(this.metadata.music);
            this.buildNewAudioDom();
        }
        else {
            this.loadDefaultMedia();
        }
    }
    checkForSermonTitle() {
        const targetNode = this.node.querySelector('[data-amelement="sermon title"] .dynamic');
        return targetNode !== null;
    }
    checkForBrandLogo() {
        const targetNode = this.node.querySelector('[data-amelement="logo"]');
        return targetNode !== null;
    }
    buildTitle() {
        const targetNode = this.node.querySelector('[data-amelement="sermon title"] .dynamic');
        if (!targetNode)
            return false;
        targetNode.style.overflow = 'visible';
        targetNode.style.height = '100%';
        targetNode.innerHTML = '';
        const dynamicNode = document.createElement('span');
        dynamicNode.innerHTML = this.metadata.title;
        dynamicNode.style.display = 'block';
        targetNode.append(dynamicNode);
        const parent = targetNode.parentElement;
    }
    buildBrandKit() {
        const logoNode = this.node.querySelector('[data-amelement="logo"]');
        if (!logoNode)
            return false;
        // logoNode.style.display = this.metadata.showBrandLogo ? 'flex' : 'none';
        logoNode.style.alignItems = 'center';
        logoNode.style.justifyContent = 'center';
        if (!this.metadata?.brandKit)
            return;
        const brandKit = this.metadata.brandKit;
        const imageNode = logoNode.querySelector('img');
        if (imageNode) {
            imageNode.style.width = '100%';
            imageNode.style.height = '100%';
            imageNode.style.objectFit = 'contain';
            try {
                if (brandKit.logoUrl && brandKit.logoUrl !== '') {
                    logoNode.style.objectFit = 'contain';
                    logoNode.querySelector('img')?.setAttribute('src', brandKit.logoUrl);
                }
            }
            catch (err) {
                console.warn('Could not apply to logo node.');
            }
        }
        if (brandKit.serviceTimes) {
            const serviceTimesNode = this.node.querySelector('[data-amelement="service times"] .dynamic');
            if (serviceTimesNode) {
                if (brandKit.serviceTimes && brandKit.servicetimes !== '') {
                    serviceTimesNode.innerHTML = brandKit.serviceTimes;
                }
            }
        }
    }
    // TODO: MOVE ALL FONT BUILDING LOADING AND RENDERING LOGIC TO TEMPLATE LOADER
    async buildFonts() {
        const onComplete = () => {
            let userTextClass = this.node.querySelectorAll('.userTextClass .dynamic');
            userTextClass.forEach((node) => {
                if (this.metadata.fontFamily) {
                    if (this.metadata.fontFamily.isUserFont) {
                        node.style.fontFamily = this.metadata.fontFamily.label.split('.')[0];
                    }
                    else if (this.metadata?.fontFamily == 'default') {
                        // Dont do anything
                    }
                    else {
                        node.style.fontFamily = this.metadata.fontFamily.value;
                    }
                }
                if (this.metadata.fontColor && this.metadata.fontColor !== 'default') {
                    node.style.color = this.metadata.fontColor;
                }
            });
            const captionNode = this.node.querySelector('[data-amelement="caption"]');
            const applications = [];
        };
        if (this.metadata.fontFamily) {
            if (this.metadata.fontFamily.isUserFont) {
                const label = this.metadata.fontFamily.label.split('.')[0];
                const url = this.metadata.fontFamily.value;
                const font = new FontFace(label, `url('${url}')`);
                font
                    .load()
                    .then(() => {
                    document.fonts.add(font);
                    onComplete();
                })
                    .catch(err => logWarning('Problem loading user font', err));
            }
            else {
                loadGoogleFont(this.metadata.fontFamily.value);
                onComplete();
            }
        }
        else {
            console.warn('revert to default font.');
        }
    }
    zoomIn(percent) {
        const finalImageToExport = this.node.querySelector('#finalImageToExport');
        if (!finalImageToExport)
            return;
        this.zoomPercentage = this.zoomPercentage + percent;
        const newWidth = parseFloat(finalImageToExport.style.width) * this.zoomPercentage;
        const newHeight = parseFloat(finalImageToExport.style.height) * this.zoomPercentage;
        finalImageToExport.style.width = newWidth + 'px';
        finalImageToExport.style.height = newHeight + 'px';
        let ratio = 1 / this.zoomPercentage;
        adjustPreviewSizes(ratio, '#finalImageToExport');
    }
    zoomOut(percent) {
        this.resetToOrigSize();
        this.zoomIn(percent);
    }
    resetToOrigSize() {
        const finalImageToExport = this.node.querySelector('#finalImageToExport');
        if (!finalImageToExport)
            return;
        const { fx, fy } = finalImageToExport.dataset;
        const _r = parseFloat(finalImageToExport.style.width) / parseFloat(fx);
        finalImageToExport.style.width = fx;
        finalImageToExport.style.height = fy;
        adjustPreviewSizes(_r, '#finalImageToExport');
    }
    async buildTemplate(template, rebuild, forProduction) {
        // console.log('building template', template);
        console.trace();
        this.templateLoader = new TemplateLoader(this.node);
        await this.templateLoader.loadHTML(template, forProduction);
        this.loadDefaultMedia();
        if (this.onCompleteTemplateLoadCallback) {
            this.onCompleteTemplateLoadCallback();
            this.onCompleteTemplateLoadCallback = undefined;
        }
        if (rebuild)
            this.rebuildChanges();
        return this;
    }
    onCompleteTemplateBuild(fn) {
        this.onCompleteTemplateLoadCallback = fn;
        return this;
    }
    adjustDisplay(forProduction = false) {
        console.info('[PRODUCTION ADJUST FOR DISPLAY]');
        if (this.adjustedForProduction)
            return;
        if (forProduction)
            this.adjustedForProduction = true;
        const templateDiv = this.node.querySelector('#templateLoaded');
        const finalImageToExport = this.node.querySelector('#finalImageToExport');
        const containerArea = this.node.querySelector('#containerArea');
        if (!templateDiv || !finalImageToExport)
            return;
        const originalWidth = Number.parseInt(templateDiv.getAttribute('bkgImageContainterWidth'));
        let expectedWidth = Number.parseInt(templateDiv?.getAttribute('fx'));
        let expectedHeight = Number.parseInt(templateDiv?.getAttribute('fy'));
        // if (expectedHeight > 1080) {
        //   // expectedWidth = Math.ceil(determineWidth(expectedWidth, expectedHeight, 1080));
        //   expectedHeight = Math.ceil(determineHeight(expectedWidth, expectedHeight, 720));
        //   // expectedHeight = 1080;
        //   expectedWidth = 720;
        //   // console.log('width and height fixed', expectedWidth, expectedHeight);
        // }
        const currentWidth = finalImageToExport.offsetWidth;
        const currentHeight = finalImageToExport.offsetHeight;
        //console.log('current width/height', currentWidth, currentHeight, expectedWidth, expectedHeight);
        finalImageToExport.style.width = expectedWidth + 'px';
        finalImageToExport.style.height = expectedHeight + 'px';
        const originalRatio = currentWidth / currentHeight;
        const newRatio = expectedWidth / expectedHeight;
        const templateScaleFactor = currentWidth / expectedWidth;
        const cloned = finalImageToExport.cloneNode(true);
        //console.log('cloned', cloned);
        //console.log('adjusting now', finalImageToExport, templateScaleFactor);
        adjustPreviewSizes(templateScaleFactor, '#finalImageToExport');
        this.templateLoader.recomputeTemplateMetadata();
        this.recomputeCaptionTextSize();
        return this;
    }
    determineExistingAudioTracks() {
        this.audioTracks = [...this.node.querySelectorAll('.audioForVideo')].map(node => {
            return node.querySelector('audio > source')?.getAttribute('src');
        });
    }
    createAudioTrack(src) {
        const audioElement = document.createElement('audio');
        const sourceElement = document.createElement('source');
        audioElement.append(sourceElement);
        audioElement.loop = true;
        sourceElement.src = src;
        this.node.append(audioElement);
        // console.log("CREATING AUDIO TRACK", { audioElement, sourceElement, src });
        return audioElement;
    }
    clearAudioTracks() {
        // console.log("CLEARING AUDIO TRACKS", { node : this.node, audioElements : this.node.querySelectorAll('audio') });
        this.node.querySelectorAll('audio').forEach(audioElement => {
            audioElement.remove();
        });
    }
    buildNewAudioDom() {
        this.clearAudioTracks();
        this.audioElements = this.audioTracks.map(track => {
            return this.createAudioTrack(track);
        });
        this.adjustAudioVolume();
    }
    ensureVideoElementLooping() {
        this.node.querySelectorAll('video').forEach(videoElement => {
            const parentElement = videoElement.parentElement;
            if (parentElement && this.hasSermonclipTag(parentElement))
                return;
            videoElement.loop = true;
        });
    }
    loadDefaultMedia() {
        this.determineExistingAudioTracks();
        this.buildNewAudioDom();
        this.ensureVideoElementLooping();
    }
    playAudio() {
        // console.log("AUDIO ELEMENTS", this.audioElements);
        this.audioElements.forEach(element => {
            if (element.paused)
                element.play();
        });
    }
    pauseAudio() {
        this.audioElements.forEach(element => {
            if (!element.paused)
                element.pause();
        });
    }
    scanLegacyCodeForAnimationObject() {
        return Array.from(this.node.querySelectorAll('.animatedElement'))
            .map(node => {
            let dataAttribute = node.getAttribute(LEGACY_ANIMATION_OBJECT);
            if (!dataAttribute)
                return false;
            // if (node.classList.contains("userTextClass")) return false;
            if (dataAttribute.includes('&quot;')) {
                dataAttribute = new DOMParser().parseFromString(dataAttribute, 'text/html').body.textContent;
            }
            if (dataAttribute) {
                let data = JSON.parse(dataAttribute);
                return { node, data };
            }
            return { node, data: {} };
        })
            .filter(item => item !== false);
    }
    isASermonClip = (node) => {
        if (node == null)
            return false;
        const isSermon = node?.getAttribute('sermonclip') && node?.getAttribute('sermonclip') == 'true' ? true : false;
        return isSermon;
    };
    hasSermonclipTag = (node) => {
        if (!node)
            return false;
        return node.getAttribute('data-amelement') == 'sermonclip';
    };
    pauseBGVideoElements() {
        this.bgVideoElements.forEach(element => {
            if (!element.paused)
                element.pause();
        });
    }
    seekBGVideoElements(seek) {
        this.bgVideoElements.forEach(element => {
            try {
                element.currentTime = seek;
            }
            catch (err) {
                console.warn('Could not seek video element', err);
            }
        });
    }
    buildLegacyAnimations() {
        this.bgVideoElements = [];
        const animations = this.scanLegacyCodeForAnimationObject();
        animations.forEach(rawAnimation => {
            if (!rawAnimation)
                return;
            rawAnimation.data.animations.forEach((animation) => {
                if (animation.animation == 'PlayAudio')
                    return;
                if (animation.animation == 'PlayVideo') {
                    if (this.isASermonClip(rawAnimation.node))
                        return;
                    const video = rawAnimation.node.querySelector('video');
                    if (video)
                        this.bgVideoElements.push(video);
                }
                let builtAnimation = allAnimationsObject[animation.animation]['function']({
                    target: rawAnimation.node,
                    targets: '#' + rawAnimation.node.id,
                    duration: Number.parseFloat(animation.animationDuration) * 1000,
                    opacity: animation?.opacity ? animation.opacity : 1
                });
                if (animation.animationDelay > this.totalDuration) {
                    const animDuration = Number.parseFloat(animation.animationDuration) * 1000;
                    const newDelay = this.totalDuration * 1000 - animDuration;
                    this.timeline.add(builtAnimation, newDelay);
                }
                else {
                    this.timeline.add(builtAnimation, animation.animationDelay * 1000);
                }
            });
        });
    }
    buildTimeline() {
        let seek = this.timeline ? this.timeline.currentTime : 1;
        if (this.timeline) {
            this.timeline.pause();
        }
        this.timeline = anime.timeline({
            autoplay: false,
            loop: false
        });
        animationPlayerInstance.setAnimationTimeline(this.timeline);
        this.buildTimelineCallbacks();
        return seek;
    }
    async forceCaptureEndpoint() {
        this.captureEndpoint = true;
    }
    buildTimelineCallbacks() {
        let checkpoint = 0;
        // this.timeline.begin = () => {
        //   checkpoint = this.timeline.duration - 50;
        //   console.log('timeline is beginning', checkpoint);
        // }
        let skipNext = false;
        this.timeline.update = () => {
            if (this.updateFn)
                this.updateFn(this.timeline.progress);
            if (checkpoint == 0)
                return (checkpoint = this.timeline.duration - 50);
            if (this.timeline.currentTime >= checkpoint && !this.captureEndpoint && !skipNext) {
                //console.log('Force pausing timeline to prevent weird onComplete Bug.');
                try {
                    skipNext = true;
                    // console.log(
                    //   'Timeline duration seek to ',
                    //   this.timeline.currentTime,
                    //   this.timeline.duration,
                    //   this.timeline.duration - 50
                    // );
                    this.timeline.pause();
                    this.timeline.seek(this.timeline.duration - 25);
                    this.pauseAudio();
                    this.animationState = 'pause';
                    window.playingStatus = 'pause';
                }
                catch (err) {
                    console.warn('Could not pause timeline', err);
                }
            }
            else {
                skipNext = false;
            }
        };
        // this.timeline.complete = () => {
        //   this.pauseAudio();
        //   this.animationState = 'pause';
        //   window.playingStatus = 'pause';
        //   if (!this.captureEndpoint) return; // Skip this last part if the capture endpoint variable is set to false
        // };
    }
    get isCompleted() {
        if (!this.timeline)
            return 0;
        return this.timeline.progress > 99;
    }
    restartTimeline(remember = false) {
        //console.log('Triggering timeline restart from the animationService');
        let seekPoint = 1;
        if (remember) {
            seekPoint = this.timeline.currentTime;
        }
        this.timeline.seek(seekPoint);
        // this.play();
    }
    buildTimelineWithoutCaptions() {
        this.animations.forEach(animatable => {
            const anim = {
                target: '',
                duration: animatable.duration
            };
            this.timeline.add(anim, animatable.delay);
        });
    }
    buildCaptionAnimations(targetNode) {
        let wordArray = [];
        if (!targetNode) {
            return this.buildTimelineWithoutCaptions();
        }
        targetNode.innerHTML = '';
        targetNode.style.height = '100%';
        targetNode.style.lineHeight = '1.2';
        const parentNodes = [];
        this.animations.forEach(animatable => {
            if (parentNodes.indexOf(animatable.parentNode) > -1)
                return;
            parentNodes.push(animatable.parentNode);
            animatable.parentNode.innerHTML = ''; // Clear any previous nodes.
        });
        this.textNodes = parentNodes;
        const maxLength = this.animations.length - 1;
        this.animations.forEach((animatable, index) => {
            const node = document.createElement('span');
            Object.assign(node.style, {
                marginRight: '0.25em',
                marginBottom: '0.3em',
                display: 'inline-block'
            });
            node.innerHTML = animatable.word;
            if (animatable.word == '' || animatable.word.length == 0)
                node.style.display = 'none';
            const onComplete = () => {
                wordArray.push(node);
                if (animatable.isLastWordInSentence && index < maxLength) {
                    animatable.parentNode.style.display = 'none';
                }
            };
            const onBegin = () => {
                animatable.parentNode.style.display = 'block';
                parentNodes.forEach(node => {
                    if (node !== animatable.parentNode)
                        node.style.display = 'none';
                    else
                        node.style.display = 'block';
                    let parent = targetNode.parentNode;
                    if (!parent) {
                        console.warn('parent node does not exist.', parent);
                        return false;
                    }
                });
            };
            let animationName = this.metadata?.textAnimation ? this.metadata.textAnimation : 'FadeInDown';
            // @ts-ignore: Unreachable code error
            const animation = allAnimationsObject[animationName]['function']({
                target: node,
                targets: node,
                duration: animatable.duration,
                opacity: 1
            });
            animation.changeComplete = onComplete;
            animation.complete = onComplete;
            animation.changeBegin = onBegin;
            animation.begin = onBegin;
            // Temporary fix for animations that are "left behind" when scrubbing or visibility change detection
            // animation.display = [{ delay: animatable.duration, value: "none" }];
            this.timeline.add(animation, animatable.delay);
            animatable.parentNode.appendChild(node);
            if (animatable.isFirstWordInSentence) {
                targetNode.append(animatable.parentNode);
            }
        });
        this.captionParentNodes = parentNodes;
        return parentNodes;
    }
    quickBuildAnimations() {
        let cachedseek = this.buildTimeline();
        this.buildLegacyAnimations();
        const targetNode = this.node.querySelector('[data-amelement="caption"] .dynamic');
        if (targetNode)
            this.buildCaptionAnimations(targetNode);
        if (cachedseek && this.timeline) {
            this.timeline.seek(cachedseek);
            this.seekVideo();
        }
    }
    buildCaptionTimeline() {
        this.captionTimeline = anime.timeline({
            autoplay: false
        });
    }
    recomputeCaptionTextSize() {
        const dimensions = this.templateLoader.templateMetadata.captionDimensions;
        if (dimensions?.width == 0 || dimensions?.height == 0 || dimensions == null)
            return;
        const parentNodes = this.captionParentNodes;
        if (parentNodes)
            parentNodes.forEach(node => applyDynamicTextScaling(node, dimensions, Boolean(dimensions ? dimensions.height * 2 : 200)));
        this.timeline?.seek(this.timeline.currentTime);
    }
    async buildAnimations(reset = false, onComplete) {
        await this.buildVideoDom();
        let cachedseek = this.buildTimeline();
        this.buildLegacyAnimations();
        const targetNode = this.node.querySelector('[data-amelement="caption"] .dynamic');
        if (!targetNode) {
            console.warn('Target node does not exist');
            setTimeout(() => {
                if (reset) {
                    if (reset === true)
                        this.timeline.seek(1);
                    else
                        this.timeline.seek(reset);
                }
                else
                    this.timeline.seek(cachedseek);
                if (onComplete)
                    setTimeout(() => {
                        onComplete();
                    }, 200);
                return false;
            });
            this.buildTimelineWithoutCaptions();
            return false;
        } // No captions
        const parentNodes = this.buildCaptionAnimations(targetNode);
        if (parentNodes)
            this.captionParentNodes = parentNodes;
        const waitForTemplateMetadata = () => new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (this.templateLoader?.templateMetadata) {
                    clearInterval(interval);
                    resolve(true);
                }
            }, 250);
        });
        setTimeout(async () => {
            await waitForTemplateMetadata();
            const dimensions = this.templateLoader.templateMetadata.captionDimensions;
            if (parentNodes)
                parentNodes.forEach(node => applyDynamicTextScaling(node, dimensions, Boolean(dimensions ? dimensions.height * 2 : 200)));
            setTimeout(() => {
                if (reset) {
                    if (reset === true)
                        this.timeline.seek(1);
                    else
                        this.timeline.seek(reset);
                }
                else
                    this.timeline.seek(cachedseek);
                if (onComplete)
                    setTimeout(() => {
                        onComplete();
                    }, 200);
            }, 500);
        }, 500);
        return this;
    }
    async buildVideoDom() {
        // Wait for this to be done
        let promises = [];
        Array.from(this.node.querySelectorAll('video')).forEach(async (node) => {
            const promise = new Promise(async (resolve, reject) => {
                if (!(node.parentElement &&
                    node.parentElement.hasAttribute('data-amelement') &&
                    node.parentElement.getAttribute('data-amelement') === 'sermonclip'))
                    return resolve(true);
                await this.loadVideo(node, 1);
                this.appendToVideoElements(node);
                const parentNode = node.parentNode;
                if (!parentNode)
                    return resolve(true);
                const containerHeight = parentNode.clientHeight;
                const containerWidth = parentNode.clientWidth;
                const videoHeight = node.videoHeight;
                const videoWidth = node.videoWidth;
                if (videoWidth == 0 || videoHeight == 0) {
                    // This means that something went wrong during the loading process. We need to alert the system to retry this fix later on.
                    // TODO: Add a retry mechanism to fix this issue when the user loads up the page again.
                }
                if (videoWidth > videoHeight || videoWidth == 0 || videoHeight == 0)
                    return resolve(true);
                const finalImageToExport = document.getElementById('finalImageToExport');
                if (finalImageToExport) {
                    parentNode.style.display = 'flex';
                    parentNode.style.justifyContent = 'center';
                }
                node.removeAttribute('style');
                node.style.width = 'auto !important';
                node.style.height = 'auto !important';
                return resolve(true);
            });
            promises.push(promise);
        });
        await Promise.all(promises);
    }
    async attemptToFetchHtmlFromProjectState(templateId, retries = 0, delay = 0) {
        console.log('attempting to fetch html template from project state');
        try {
            return await new TemplatesService().getTemplate(templateId);
        }
        catch (err) {
            if (retries < 3) {
                await this.delay(delay);
                return await this.attemptToFetchHtmlFromProjectState(templateId, retries + 1, delay + 500);
            }
            throw new Error('Could not get template');
        }
    }
    async getHTMLTemplateFromServer(templateId) {
        const template = await this.attemptToFetchHtmlFromProjectState(templateId);
        if (!template.content)
            throw new NoTemplateHTMLContentError();
        return template.content;
    }
    async getOriginalHTML(projectState) {
        if (!projectState.template)
            throw new NoTemplateObjectError();
        //console.warn('project template', projectState.template);
        if (projectState.template.content) {
            console.warn('project has template');
            return projectState.template.content;
        }
        if (!projectState.originalTemplateHTML) {
            console.log('Template is being fetched from server...');
            return this.getHTMLTemplateFromServer(projectState.template.id);
        }
        return projectState.originalTemplateHTML;
    }
    async attemptToResetVideoObject(projectState) {
        let html = await this.getOriginalHTML(projectState);
        const dom = document.createElement('div');
        dom.innerHTML = html;
    }
    getSermonClipElement() {
        const sermonclip = this.node.querySelector('[sermonclip="true"]');
        return sermonclip;
    }
    attachClassToSermonClip(...classes) {
        const targetNodes = this.node.querySelectorAll('[data-amelement="sermonclip"]');
        for (const targetNode of targetNodes) {
            targetNode.classList.add(...classes);
        }
    }
    detachClassFromSermonClip(...classes) {
        const targetNodes = this.node.querySelectorAll('[data-amelement="sermonclip"]');
        for (const targetNode of targetNodes) {
            targetNode.classList.remove(...classes);
        }
    }
    async waitForSermonClip() {
        return new Promise((resolve, reject) => {
            let totalCount = 0;
            const finish = () => {
                clearInterval(interval);
                return resolve(true);
            };
            const interval = setInterval(() => {
                if (this.sermonClip) {
                    return finish();
                }
                else if (totalCount > 100) {
                    console.warn('[Total count of sermon check reached.]');
                    return finish();
                }
                totalCount++;
            }, 250);
        });
    }
}
/**
 * These functions are ported over from my work over at the preview browser (face tracking trial feature)
 * The ultimate goal is to clean up this code and have a dedicated class for this. for now we are writing it here for experimental purposes to make it easier
 */
function getVideoDimensions(video) {
    const { width, height } = video.getBoundingClientRect();
    return { width, height };
}
function getXPos(x_pos, videoDimensions, containerDimensions) {
    const minContainerDimensionReposition = containerDimensions.width / 2;
    const maxRepos = Math.abs(containerDimensions.width - videoDimensions.width);
    const absoluteVideoPos = x_pos * videoDimensions.width;
    const target = absoluteVideoPos - minContainerDimensionReposition;
    if (target < 0)
        return 0;
    if (target + containerDimensions.width > videoDimensions.width)
        return -maxRepos;
    return -target;
}
const animationService = new AnimationService();
export const getInstance = () => animationService;
export default AnimationService;
